import { createDocEntryStore } from './docStore';
import { asHero, emptyInfo, loadInfo, saveInfo, type NoticeInfo } from './realImages';

// The manual's general info (brand + estimated time + number of people), read by the
// sections call and shared (reactively) across Phase 1 — shown read-only — and the
// real-image search, which uses the brand to qualify the query. Module scope +
// localStorage so it survives navigation, like the other notice stores.
type Snapshot = NoticeInfo & { detecting: boolean };

const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: { ...loadInfo(docId), detecting: false },
}));

// Merge a partial into this document's snapshot and re-render — the shared helper.
const update = store.patch;

// Whether a read said anything at all: whether Phase 1's bar counts this read as landed.
export function hasInfo(info: Partial<NoticeInfo>): boolean {
  return !!(
    info.brand || info.productName || info.time || info.people || info.maxWeight ||
    info.category || info.site || info.queries?.length || info.reasoning || info.raw
  );
}

// THE INFO ARRIVES WITH THE SECTIONS: every sections block reads it off its own pages
// (categoryRun), so a manual of several blocks answers it several times. Each field keeps
// the first block that filled it — a max weight printed only on the last page is still
// found, and a brand the cover gave is not overwritten by a later page's silence.
//
// `fresh` is a new read: its first answer replaces what the last read left. A resumed one
// fills only the fields still empty, like the blocks it does not re-ask.
const freshRead = new Set<string>();

export function startInfoRead(docId: string, fresh: boolean) {
  if (fresh) freshRead.add(docId);
  else freshRead.delete(docId);
  update(docId, { detecting: true });
}

export function mergeInfo(docId: string, answer: Partial<NoticeInfo>) {
  const { detecting: _detecting, ...current } = store.getEntry(docId).snapshot;
  const base = freshRead.delete(docId) ? emptyInfo() : current;
  const fill = (k: keyof Omit<NoticeInfo, 'queries' | 'heroPage' | 'heroBox' | 'reasoning' | 'raw'>) =>
    base[k] || (answer[k] || '').trim();
  const info: NoticeInfo = {
    brand: fill('brand'), productName: fill('productName'), time: fill('time'),
    people: fill('people'), maxWeight: fill('maxWeight'), category: fill('category'),
    site: fill('site'),
    queries: base.queries.length ? base.queries : (answer.queries || []),
    ...(base.heroPage ? asHero(base.heroPage, base.heroBox) : asHero(answer.heroPage, answer.heroBox)),
    reasoning: [base.reasoning, answer.reasoning || ''].filter(Boolean).join('\n\n'),
    raw: [base.raw, answer.raw || ''].filter(Boolean).join('\n\n'),
  };
  saveInfo(docId, info);
  update(docId, info);
}

export function endInfoRead(docId: string) {
  freshRead.delete(docId);
  update(docId, { detecting: false });
}

// A field typed into the Phase 1 info table. Stored verbatim and persisted, independent
// of the read — so it can be edited even while the pipeline is processing later steps. A
// later read overwrites it, like the other detected fields.
export function setInfoField(docId: string, patch: Partial<Omit<NoticeInfo, 'reasoning' | 'raw'>>) {
  const { detecting: _detecting, ...info } = store.getEntry(docId).snapshot;
  saveInfo(docId, { ...info, ...patch });
  update(docId, patch);
}

// What the MANUAL says — one of the two candidates for the brand / product name /
// finished-product picture, not the final answer. The resolved value (this
// reconciled with the web listing) comes from infoPicks.
export const getBrandInfo = store.get;

export const useBrand = store.useSnapshot;
